import { cn } from '@/lib/cn'
import { formatCurrency, formatDiscount, priceParts } from '@/lib/format'

interface PriceProps {
  value: number
  originalValue?: number | null
  discountPercent?: number | null
  size?: 'sm' | 'md' | 'lg' | 'xl'
  /** `chip` desenha o desconto em pílula amarela (usado no grid de busca). */
  discountStyle?: 'text' | 'chip'
  className?: string
}

/**
 * Os tamanhos maiores encolhem até `lg`: num card de meia tela o preço em 28 ou
 * 36px domina tudo à volta, que é texto de 13px. Acima de 1024px valem os
 * tamanhos do layout de referência.
 */
const SIZES = {
  sm: { price: 'text-lg', cents: 'text-[10px]', old: 'text-[11px]', off: 'text-[11px]' },
  md: {
    price: 'text-[19px] lg:text-[22px]',
    cents: 'text-[11px]',
    old: 'text-xs',
    off: 'text-xs',
  },
  lg: {
    price: 'text-[22px] lg:text-[28px]',
    cents: 'text-[11px] lg:text-sm',
    old: 'text-[13px]',
    off: 'text-[13px]',
  },
  xl: {
    price: 'text-[28px] lg:text-[36px]',
    cents: 'text-[13px] lg:text-base',
    old: 'text-sm',
    off: 'text-sm',
  },
} as const

/**
 * Preço no formato do layout: centavos sobrescritos, preço "de" riscado acima e
 * o percentual de desconto em verde ao lado.
 */
export function Price({
  value,
  originalValue,
  discountPercent,
  size = 'md',
  discountStyle = 'text',
  className,
}: PriceProps) {
  const { integer, cents } = priceParts(value)
  const discount = formatDiscount(discountPercent)
  const styles = SIZES[size]
  const showOriginal = originalValue != null && originalValue > value

  return (
    <div className={cn('flex flex-col gap-0.5', className)}>
      {(showOriginal || (discount && discountStyle === 'chip')) && (
        <div className="flex items-center justify-between gap-2">
          {showOriginal ? (
            <s className={cn('text-ink-faint', styles.old)}>
              {formatCurrency(originalValue)}
            </s>
          ) : (
            <span />
          )}
          {discount && discountStyle === 'chip' && (
            <span
              className={cn(
                'rounded-sm bg-accent-soft px-1.5 py-0.5 font-semibold text-ink',
                styles.off,
              )}
            >
              {discount}
            </span>
          )}
        </div>
      )}

      <div className="flex items-baseline gap-2">
        {/* O preço acessível fica no aria-label; o visual usa o sobrescrito. */}
        <span
          className={cn('font-medium leading-none text-ink', styles.price)}
          aria-label={formatCurrency(value)}
        >
          <span aria-hidden="true">
            R$ {integer}
            {cents && <sup className={cn('ml-px align-super', styles.cents)}>{cents}</sup>}
          </span>
        </span>

        {discount && discountStyle === 'text' && (
          <span className={cn('font-semibold text-brand', styles.off)}>{discount}</span>
        )}
      </div>
    </div>
  )
}
